import recordConstants from '../../../constants/record';
import { IRecord, Record as RecordHelper } from '../../../modules/bin/utility';
import { DEFAULT_QUEUE_FILTERS, FanOutResult } from '../models';
import {
  Action,
  CM_BODYHASH_FETCH_FAILURE,
  CM_BODYHASH_FETCH_REQUESTED,
  CM_BODYHASH_FETCH_SUCCESS,
  CM_CLUSTER_FETCH_FAILURE,
  CM_CLUSTER_FETCH_REQUESTED,
  CM_CLUSTER_FETCH_SUCCESS,
  CM_DECISION_RESET_FAILURE,
  CM_DECISION_RESET_REQUESTED,
  CM_DECISION_RESET_SUCCESS,
  CM_DECISION_SUBMIT_FAILURE,
  CM_DECISION_SUBMIT_REQUESTED,
  CM_DECISION_SUBMIT_SUCCESS,
  CM_DETAIL_CLEAR,
  CM_DETAIL_FETCH_FAILURE,
  CM_DETAIL_FETCH_REQUESTED,
  CM_DETAIL_FETCH_SUCCESS,
  CM_FANOUT_EVENT_RESULT,
  CM_FANOUT_FINISH,
  CM_FANOUT_RESET,
  CM_FANOUT_SELECT_SET,
  CM_FANOUT_SELECT_TOGGLE,
  CM_FANOUT_START,
  CM_KEYBOARD_SET,
  CM_LOCK_ACQUIRE_FAILURE,
  CM_LOCK_ACQUIRE_HELD,
  CM_LOCK_ACQUIRE_HELD_BY_OTHER,
  CM_LOCK_ACQUIRE_REQUESTED,
  CM_LOCK_HEARTBEAT_FAILURE,
  CM_LOCK_HEARTBEAT_SUCCESS,
  CM_LOCK_RELEASE,
  CM_MODAL_CLOSE,
  CM_MODAL_OPEN,
  CM_QUEUE_APPEND_FAILURE,
  CM_QUEUE_APPEND_REQUESTED,
  CM_QUEUE_APPEND_SUCCESS,
  CM_QUEUE_FETCH_FAILURE,
  CM_QUEUE_FETCH_REQUESTED,
  CM_QUEUE_FETCH_SUCCESS,
  CM_QUEUE_FILTERS_SET,
  ContentModerationState,
  FanOutSlice,
  LockSlice,
  QueueSlice,
  UiSlice,
} from './contentModerationTypes';

function recordLoading(prev?: IRecord): IRecord {
  const base = prev ?? RecordHelper.getDefaultState();
  return {
    ...base,
    ready: recordConstants.RECORD_STATUS_LOADING,
    error: { show: false, errorKeys: null },
    success: { show: false },
  };
}

function recordSuccess<T>(data: T): IRecord {
  return {
    id: null,
    ready: recordConstants.RECORD_STATUS_READY,
    error: { show: false, errorKeys: null },
    success: { show: true },
    data,
    state: {},
  };
}

function recordFailure(errorKey: string, param: string): IRecord {
  return {
    id: null,
    ready: recordConstants.RECORD_STATUS_ERROR,
    error: { show: true, errorKeys: [errorKey], param },
    success: { show: false },
    data: {},
    state: {},
  };
}

const defaultQueue = (): QueueSlice => ({
  items: [],
  filters: DEFAULT_QUEUE_FILTERS,
  totalCount: 0,
  page: DEFAULT_QUEUE_FILTERS.page,
  pageSize: DEFAULT_QUEUE_FILTERS.pageSize,
  isLoading: false,
  isPolling: false,
  isAppending: false,
  error: null,
  lastFetchedAt: null,
});

const defaultLock = (): LockSlice => ({
  state: 'Idle',
  holder: null,
  lastHeartbeatAt: null,
  heartbeatFailures: 0,
  error: null,
});

const defaultFanOut = (): FanOutSlice => ({
  selectedEventIds: [],
  isRunning: false,
  results: {},
});

const defaultUi = (): UiSlice => ({ modal: 'none', keyboardEnabled: true });

export function makeInitialState(): ContentModerationState {
  return {
    queue: defaultQueue(),
    detail: RecordHelper.getDefaultState(),
    cluster: RecordHelper.getDefaultState(),
    bodyHashCluster: RecordHelper.getDefaultState(),
    decisionSubmit: RecordHelper.getDefaultState(),
    decisionReset: RecordHelper.getDefaultState(),
    lock: defaultLock(),
    fanOut: defaultFanOut(),
    ui: defaultUi(),
  };
}

export function contentModerationReducer(
  state: ContentModerationState = makeInitialState(),
  action: Action,
): ContentModerationState {
  switch (action.type) {
    // Queue
    case CM_QUEUE_FETCH_REQUESTED:
      return {
        ...state,
        queue: {
          ...state.queue,
          isLoading: action.isPoll ? state.queue.isLoading : true,
          isPolling: action.isPoll,
          error: null,
        },
      };
    case CM_QUEUE_FETCH_SUCCESS:
      return {
        ...state,
        queue: {
          ...state.queue,
          items: action.items,
          totalCount: action.totalCount,
          page: action.page,
          pageSize: action.pageSize,
          isLoading: false,
          isPolling: false,
          error: null,
          lastFetchedAt: action.at,
        },
      };
    case CM_QUEUE_FETCH_FAILURE:
      return {
        ...state,
        queue: {
          ...state.queue,
          isLoading: false,
          isPolling: false,
          error: action.error,
        },
      };
    case CM_QUEUE_APPEND_REQUESTED:
      return {
        ...state,
        queue: { ...state.queue, isAppending: true, error: null },
      };
    case CM_QUEUE_APPEND_SUCCESS:
      return {
        ...state,
        queue: {
          ...state.queue,
          // Append rather than replace. Update echoed paging metadata so
          // hasMore (items.length < totalCount) reflects the latest server view.
          items: [...state.queue.items, ...action.items],
          totalCount: action.totalCount,
          page: action.page,
          pageSize: action.pageSize,
          isAppending: false,
          error: null,
        },
      };
    case CM_QUEUE_APPEND_FAILURE:
      return {
        ...state,
        queue: { ...state.queue, isAppending: false, error: action.error },
      };
    case CM_QUEUE_FILTERS_SET:
      return {
        ...state,
        queue: { ...state.queue, filters: action.filters },
      };

    // Detail
    case CM_DETAIL_FETCH_REQUESTED:
      return { ...state, detail: recordLoading(state.detail) };
    case CM_DETAIL_FETCH_SUCCESS:
      return { ...state, detail: recordSuccess(action.detail) };
    case CM_DETAIL_FETCH_FAILURE:
      return { ...state, detail: recordFailure(action.error.error, action.error.param) };
    case CM_DETAIL_CLEAR:
      return {
        ...state,
        detail: RecordHelper.getDefaultState(),
        cluster: RecordHelper.getDefaultState(),
        bodyHashCluster: RecordHelper.getDefaultState(),
        decisionSubmit: RecordHelper.getDefaultState(),
        decisionReset: RecordHelper.getDefaultState(),
        lock: defaultLock(),
        fanOut: defaultFanOut(),
        ui: defaultUi(),
      };

    // Cluster
    case CM_CLUSTER_FETCH_REQUESTED:
      return { ...state, cluster: recordLoading(state.cluster) };
    case CM_CLUSTER_FETCH_SUCCESS:
      return { ...state, cluster: recordSuccess(action.cluster) };
    case CM_CLUSTER_FETCH_FAILURE:
      return { ...state, cluster: recordFailure(action.error.error, action.error.param) };

    // Body-hash cluster
    case CM_BODYHASH_FETCH_REQUESTED:
      return { ...state, bodyHashCluster: recordLoading(state.bodyHashCluster) };
    case CM_BODYHASH_FETCH_SUCCESS:
      return {
        ...state,
        bodyHashCluster: recordSuccess(action.items),
        fanOut: {
          ...state.fanOut,
          selectedEventIds: action.items.map((i) => i.contentModerationEventId),
        },
      };
    case CM_BODYHASH_FETCH_FAILURE:
      return {
        ...state,
        bodyHashCluster: recordFailure(action.error.error, action.error.param),
      };

    // Lock
    case CM_LOCK_ACQUIRE_REQUESTED:
      return {
        ...state,
        lock: { ...state.lock, state: 'Acquiring', error: null },
      };
    case CM_LOCK_ACQUIRE_HELD:
      return {
        ...state,
        lock: {
          state: 'Held',
          holder: action.holder,
          lastHeartbeatAt: action.lastHeartbeatAt,
          heartbeatFailures: 0,
          error: null,
        },
      };
    case CM_LOCK_ACQUIRE_HELD_BY_OTHER:
      return {
        ...state,
        lock: {
          state: 'HeldByOther',
          holder: action.holder,
          lastHeartbeatAt: action.lastHeartbeatAt,
          heartbeatFailures: 0,
          error: null,
        },
      };
    case CM_LOCK_ACQUIRE_FAILURE:
      return {
        ...state,
        lock: { ...state.lock, state: 'Idle', error: action.error },
      };
    case CM_LOCK_HEARTBEAT_SUCCESS:
      return {
        ...state,
        lock: {
          ...state.lock,
          state: 'Held',
          lastHeartbeatAt: action.lastHeartbeatAt,
          heartbeatFailures: 0,
        },
      };
    case CM_LOCK_HEARTBEAT_FAILURE: {
      const nextFailures = state.lock.heartbeatFailures + 1;
      return {
        ...state,
        lock: {
          ...state.lock,
          state: nextFailures >= 2 ? 'HeartbeatFailed' : state.lock.state,
          heartbeatFailures: nextFailures,
          error: action.error,
        },
      };
    }
    case CM_LOCK_RELEASE:
      return { ...state, lock: defaultLock() };

    // Decision
    case CM_DECISION_SUBMIT_REQUESTED:
      return { ...state, decisionSubmit: recordLoading(state.decisionSubmit) };
    case CM_DECISION_SUBMIT_SUCCESS:
      return {
        ...state,
        decisionSubmit: recordSuccess({
          decision: action.decision,
          warnings: action.warnings,
        }),
      };
    case CM_DECISION_SUBMIT_FAILURE:
      return {
        ...state,
        decisionSubmit: recordFailure(action.error.error, action.error.param),
      };
    case CM_DECISION_RESET_REQUESTED:
      return { ...state, decisionReset: recordLoading(state.decisionReset) };
    case CM_DECISION_RESET_SUCCESS:
      return { ...state, decisionReset: recordSuccess(true) };
    case CM_DECISION_RESET_FAILURE:
      return {
        ...state,
        decisionReset: recordFailure(action.error.error, action.error.param),
      };

    // Fan-out
    case CM_FANOUT_SELECT_SET:
      return {
        ...state,
        fanOut: { ...state.fanOut, selectedEventIds: action.eventIds },
      };
    case CM_FANOUT_SELECT_TOGGLE: {
      const present = state.fanOut.selectedEventIds.includes(action.eventId);
      const selectedEventIds = present
        ? state.fanOut.selectedEventIds.filter((id) => id !== action.eventId)
        : [...state.fanOut.selectedEventIds, action.eventId];
      return {
        ...state,
        fanOut: { ...state.fanOut, selectedEventIds },
      };
    }
    case CM_FANOUT_START: {
      const results: Record<number, FanOutResult> = {};
      for (const id of action.eventIds) {
        results[id] = { eventId: id, status: 'submitting' };
      }
      return {
        ...state,
        fanOut: {
          selectedEventIds: action.eventIds,
          isRunning: true,
          results,
        },
      };
    }
    case CM_FANOUT_EVENT_RESULT:
      return {
        ...state,
        fanOut: {
          ...state.fanOut,
          results: {
            ...state.fanOut.results,
            [action.result.eventId]: action.result,
          },
        },
      };
    case CM_FANOUT_FINISH:
      return {
        ...state,
        fanOut: { ...state.fanOut, isRunning: false },
      };
    case CM_FANOUT_RESET:
      return { ...state, fanOut: defaultFanOut() };

    // UI
    case CM_MODAL_OPEN:
      return { ...state, ui: { ...state.ui, modal: action.modal } };
    case CM_MODAL_CLOSE:
      return { ...state, ui: { ...state.ui, modal: 'none' } };
    case CM_KEYBOARD_SET:
      return { ...state, ui: { ...state.ui, keyboardEnabled: action.enabled } };

    default: {
      const _exhaustiveCheck: never = action;
      void _exhaustiveCheck;
      return state;
    }
  }
}

export default contentModerationReducer;
